import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MotionConfig } from 'motion/react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router'

import { ApiError } from '../api/client'
import { ToastProvider } from '../components/feedback'
import { LegacyAdminRedirect, PlatformArea, StartRedirect } from '../features/platform/PlatformArea'
import { PLATFORM_HOME, TENANT_HOME } from './area'
import { hideSplash } from './splash'
import { Layout } from './Layout'
import { NotFound } from './NotFound'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // input/permission errors (4xx) are final; network (0) and server (5xx) errors are retried
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
})

/** Routes follow the seven process steps of the workbook (Blatt „Anleitung"). */
const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <StartRedirect /> },
      // dashboard (Übersicht) of the municipality, before step 1 (map „Plattform-Bereich“, ticket 06)
      { path: 'dashboard', lazy: async () => ({ Component: (await import('../features/dashboard/DashboardPage')).DashboardPage }) },
      { path: 'uebersicht', element: <Navigate to={TENANT_HOME} replace /> },
      // 1 – measures (overview read-only, editing in the guided workflow, ticket 07)
      { path: 'massnahmen', lazy: async () => ({ Component: (await import('../features/measures/MeasuresPage')).MeasuresPage }) },
      // one route for "neu" and an existing measure: the wizard stays mounted when its first save
      // creates the measure (two routes remounted it and lost what was typed into the next step)
      { path: 'massnahmen/:measureId', lazy: async () => ({ Component: (await import('../features/measures/MeasureWizard')).MeasureWizard }) },
      // 2 + 5 – assessment schemes
      { path: 'schema/priorisierung', lazy: async () => ({ Component: (await import('../features/schemes/SchemePage')).PrioritizationSchemePage }) },
      { path: 'schema/foerderung', lazy: async () => ({ Component: (await import('../features/schemes/SchemePage')).FundingSchemePage }) },
      // 3 – prioritization
      { path: 'priorisierung', lazy: async () => ({ Component: (await import('../features/prioritization/PrioritizationPage')).PrioritizationPage }) },
      // 4 – funding programs
      { path: 'foerderprogramme', lazy: async () => ({ Component: (await import('../features/programs/ProgramsPage')).ProgramsPage }) },
      { path: 'foerderprogramme/neu', lazy: async () => ({ Component: (await import('../features/programs/ProgramEditor')).ProgramEditor }) },
      { path: 'foerderprogramme/:programId', lazy: async () => ({ Component: (await import('../features/programs/ProgramEditor')).ProgramEditor }) },
      // 6 – funding assessment per measure, program comparison
      { path: 'foerderbewertung', lazy: async () => ({ Component: (await import('../features/funding/FundingOverviewPage')).FundingOverviewPage }) },
      { path: 'foerderbewertung/:measureId', lazy: async () => ({ Component: (await import('../features/funding/ComparisonPage')).ComparisonPage }) },
      { path: 'foerderbewertung/:measureId/neu', lazy: async () => ({ Component: (await import('../features/funding/AssessmentWizard')).AssessmentWizard }) },
      { path: 'foerderbewertung/:measureId/:assessmentId', lazy: async () => ({ Component: (await import('../features/funding/AssessmentWizard')).AssessmentWizard }) },
      // 7 – results, committee report
      { path: 'ergebnis', lazy: async () => ({ Component: (await import('../features/results/ResultsPage')).ResultsPage }) },
      // change log (Änderungsprotokoll) of the municipality, readable by every member
      { path: 'aenderungen', lazy: async () => ({ Component: (await import('../features/changes/ChangesPage')).ChangesPage }) },
      // settings of the municipality
      { path: 'einstellungen', lazy: async () => ({ Component: (await import('../features/settings/SettingsPage')).SettingsPage }) },
      // platform (map „Plattform-Bereich“): the area of the platform administration
      {
        path: 'plattform',
        element: <PlatformArea />,
        children: [
          { index: true, element: <Navigate to={PLATFORM_HOME} replace /> },
          { path: 'dashboard', lazy: async () => ({ Component: (await import('../features/platform/PlatformOverviewPage')).PlatformOverviewPage }) },
          { path: 'kommunen/:kennung', lazy: async () => ({ Component: (await import('../features/platform/TenantDetailPage')).TenantDetailPage }) },
          { path: 'verwaltung', lazy: async () => ({ Component: (await import('../features/platform/AdministrationPage')).AdministrationPage }) },
        ],
      },
      { path: 'verwaltung', element: <LegacyAdminRedirect /> },
      { path: '*', element: <NotFound /> },
    ],
  },
  // print view without app chrome
  { path: '/gremienvorlage', lazy: async () => ({ Component: (await import('../features/results/CommitteeReportPage')).CommitteeReportPage }) },
])

// the committee report renders without the app frame; it hides the start screen itself
if (window.location.pathname.startsWith('/gremienvorlage')) window.setTimeout(hideSplash, 300)

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </MotionConfig>
    </QueryClientProvider>
  )
}
